import { z } from 'zod';
/**
 * Hosted email: the shapes both halves of the product agree on.
 *
 * The deal this module describes is simple and old. A company owns a domain.
 * They point its MX at us, publish the records that let the rest of the
 * internet believe mail claiming to be from them really is, and in exchange
 * we run the mailboxes. Everything here is the vocabulary for that: what a
 * domain looks like while it is being proved, what a mailbox is, and what the
 * platform is willing to sell each workspace.
 *
 * Deliberately free of Mongo, Express and React so the DNS instructions a
 * company reads on screen are generated by the same function the server
 * verifies against — a copy of that list that drifted would have companies
 * publishing records we no longer check for, which is the worst possible bug
 * in this feature: silent, and it looks like a delivery problem.
 */
/* ------------------------------- domains -------------------------------- */
/**
 * Where a domain is in its life.
 *
 * `pending` and `verified` are the two states that matter; `suspended` is the
 * platform's hand on the switch, and is kept distinct from deletion because a
 * company behind on its bill should get its mail back when it pays, not a
 * rebuilt domain with new DKIM keys.
 */
export const MAIL_DOMAIN_STATUSES = ['pending', 'verified', 'suspended'];
/**
 * Whether the platform has agreed to host this domain at all.
 *
 * Separate from `status`, and it has to be: DNS says whether a domain *can*
 * receive mail here, and this says whether it *may*. A company that proves it
 * owns `example.gov` has proved only that — the decision to carry their mail,
 * and to put our sending reputation behind it, is ours.
 *
 * It is also the only place in the product where a tenant's own action waits
 * on a human. That is deliberate: the alternative is finding out what we
 * agreed to host by reading a spam report.
 */
export const MAIL_APPROVALS = ['pending', 'approved', 'rejected'];
/**
 * The kinds of record a company publishes, in the order the UI lists them.
 *
 * Ordered by what breaks without it: ownership proves the domain is theirs,
 * MX is what makes mail arrive at all, and the three authentication records
 * are what stop everyone else's spam filter from binning what they send.
 */
export const MAIL_RECORD_KINDS = [
    'ownership',
    'mx',
    'spf',
    'dkim',
    'dmarc',
    'autoconfig',
];
/* ------------------------------ mailboxes ------------------------------- */
/**
 * `suspended` keeps the mail and refuses the login, which is what an employer
 * wants the hour someone leaves: nothing is lost, nothing is read, and the
 * decision about the archive can wait for a calmer day.
 */
export const MAILBOX_STATUSES = ['active', 'suspended'];
export const MAIL_LIMIT_DEFAULTS = {
    maxDomains: 1,
    maxMailboxes: 10,
    maxAliasesPerMailbox: 5,
    defaultQuotaMb: 2048,
    maxQuotaMb: 25600,
    dailySendLimit: 500,
};
/** The prefix every ownership TXT is published under. */
export const MAIL_VERIFY_PREFIX = '_itsappy-verify';
/** The token's own prefix inside the record value, so the record is self-describing. */
export const MAIL_VERIFY_TAG = 'itsappy-verify=';
/**
 * Every record a company must publish, ready to render or to check.
 *
 * The single source of truth for both. `dkimPublicKey` is the base64 body of
 * an RSA public key with no PEM header, wrapping or newlines — what actually
 * goes after `p=`.
 */
export function mailDnsRecords(domain, options) {
    const { verificationToken, dkimSelector, dkimPublicKey, host } = options;
    const records = [
        {
            kind: 'ownership',
            type: 'TXT',
            name: `${MAIL_VERIFY_PREFIX}.${domain}`,
            value: `${MAIL_VERIFY_TAG}${verificationToken}`,
            required: true,
            purpose: 'Proves the domain is yours. Nothing is provisioned until this resolves — otherwise anyone could claim anyone.',
        },
        {
            kind: 'mx',
            type: 'MX',
            name: domain,
            value: host.mailHost,
            priority: 10,
            required: true,
            purpose: 'Where the rest of the internet delivers mail addressed to this domain.',
        },
    ];
    if (host.backupHost) {
        records.push({
            kind: 'mx',
            type: 'MX',
            name: domain,
            value: host.backupHost,
            priority: 20,
            required: false,
            purpose: 'A second delivery route, used only while the first is unreachable.',
        });
    }
    records.push({
        kind: 'spf',
        type: 'TXT',
        name: domain,
        value: `v=spf1 include:${host.spfHost} -all`,
        required: true,
        purpose: 'Names us as the only sender allowed to use this domain. If you send from anywhere else too — a newsletter tool, an app — add its include before the -all.',
    }, {
        kind: 'dkim',
        type: 'TXT',
        name: `${dkimSelector}._domainkey.${domain}`,
        value: `v=DKIM1; k=rsa; p=${dkimPublicKey}`,
        required: true,
        purpose: 'The public half of the key we sign your outgoing mail with. Without it, receivers cannot tell your mail from a forgery.',
    }, {
        kind: 'dmarc',
        type: 'TXT',
        name: `_dmarc.${domain}`,
        value: host.dmarcReportTo
            ? `v=DMARC1; p=quarantine; rua=mailto:${host.dmarcReportTo}; adkim=r; aspf=r; pct=100`
            : 'v=DMARC1; p=quarantine; adkim=r; aspf=r; pct=100',
        required: false,
        purpose: 'Tells receivers what to do with mail that fails the two checks above. Start at quarantine; move to p=reject once you are confident nothing legitimate is failing.',
    }, {
        kind: 'autoconfig',
        type: 'CNAME',
        name: `autoconfig.${domain}`,
        value: host.mailHost,
        required: false,
        purpose: 'Lets Outlook, Thunderbird and Apple Mail find the right server from the address alone, so nobody has to be told a port number.',
    });
    return records;
}
/* ------------------------------ validation ------------------------------ */
/**
 * Local parts we refuse to hand to anybody.
 *
 * `postmaster` and `abuse` are required by RFC 2142 to reach a human who can
 * act, and `dmarc`/`noreply` are ours: a company that gave away `dmarc@` would
 * be routing its own authentication reports into a sales inbox. They are
 * created for the domain automatically instead, as aliases onto the first
 * mailbox.
 */
export const RESERVED_LOCAL_PARTS = [
    'postmaster',
    'abuse',
    'hostmaster',
    'webmaster',
    'dmarc',
    'mailer-daemon',
];
export const MAX_LOCAL_PART_LENGTH = 64;
/**
 * A conservative local part: letters, digits, and the three separators that
 * every mail system agrees on, never at either end.
 *
 * Narrower than RFC 5321 allows on purpose. The RFC permits quoted strings
 * containing spaces and `@`, which every downstream parser handles slightly
 * differently, and an address that only works in some clients is worse than
 * an address the company was told to spell another way.
 */
export function isValidLocalPart(value) {
    if (!value || value.length > MAX_LOCAL_PART_LENGTH)
        return false;
    return /^[a-z0-9]([a-z0-9._-]*[a-z0-9])?$/.test(value) && !value.includes('..');
}
/** Lowercased, trimmed, stripped of a trailing dot and any protocol someone pasted. */
export function normalizeDomain(value) {
    return value
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/\/.*$/, '')
        .replace(/\.$/, '');
}
/**
 * A registrable domain, not a hostname.
 *
 * At least two labels, so `localhost` and a bare TLD are refused, and no
 * underscores — a company that types `mail.acme.com` here would be asking us
 * to host a subdomain, which works, but typing it by accident is far more
 * common than meaning it, and the MX they publish would be on the wrong name.
 */
export function isValidMailDomain(value) {
    if (value.length > 253)
        return false;
    return /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(value);
}
/** How many mailboxes are left, or null when the plan sets no ceiling. */
export function mailboxesRemaining(limits, usage) {
    if (!limits.maxMailboxes)
        return null;
    return Math.max(0, limits.maxMailboxes - usage.mailboxes);
}
/* -------------------------------- schemas -------------------------------- */
export const mailDomainSchema = z.object({
    domain: z
        .string()
        .transform(normalizeDomain)
        .refine(isValidMailDomain, 'Enter a domain like acme.com, without http:// or a path'),
});
const localPartSchema = z
    .string()
    .transform((value) => value.trim().toLowerCase())
    .refine(isValidLocalPart, 'Use letters, numbers, dots, dashes and underscores; start and end with a letter or number')
    .refine((value) => !RESERVED_LOCAL_PARTS.includes(value), 'That address is reserved — it is created for the domain automatically');
const mailAddressSchema = z
    .string()
    .transform((value) => value.trim().toLowerCase())
    .pipe(z.string().email('Enter a full email address'));
export const mailboxCreateSchema = z.object({
    domainId: z.string().min(1),
    localPart: localPartSchema,
    displayName: z.string().trim().max(80).default(''),
    /**
     * Supplied by the browser, never generated here: the server must be able to
     * hand the first password to whoever created the mailbox exactly once, and a
     * password the server invented is a password the server logged.
     */
    password: z.string().min(12, 'Use at least 12 characters').max(200),
    quotaMb: z.number().int().min(1).max(1024 * 1024).optional(),
    /** The Kumo member this mailbox belongs to, when it belongs to one. */
    userId: z.string().optional(),
});
export const mailboxUpdateSchema = z.object({
    displayName: z.string().trim().max(80).optional(),
    status: z.enum(MAILBOX_STATUSES).optional(),
    quotaMb: z.number().int().min(1).max(1024 * 1024).optional(),
    userId: z.string().nullable().optional(),
    aliases: z.array(localPartSchema).max(50).optional(),
    forwardTo: z.array(mailAddressSchema).max(10).optional(),
    keepOnForward: z.boolean().optional(),
});
export const mailboxPasswordSchema = z.object({
    password: z.string().min(12, 'Use at least 12 characters').max(200),
});
export const mailLimitsSchema = z.object({
    maxDomains: z.number().int().min(0).max(1000),
    maxMailboxes: z.number().int().min(0).max(100_000),
    maxAliasesPerMailbox: z.number().int().min(0).max(200),
    defaultQuotaMb: z.number().int().min(1).max(1024 * 1024),
    maxQuotaMb: z.number().int().min(1).max(1024 * 1024),
    dailySendLimit: z.number().int().min(0).max(1_000_000),
});
/** The owner's per-domain cap. Zero means the domain shares the workspace pool. */
export const mailDomainLimitSchema = z.object({
    mailboxLimit: z.number().int().min(0).max(100_000),
});
/** A platform admin's decision on a domain. */
export const mailApprovalSchema = z.object({
    approved: z.boolean(),
    /**
     * Required on a rejection and shown to the company as written, so it has to
     * be a sentence someone would be willing to sign. A refusal with no reason
     * is a support ticket by other means.
     */
    reason: z.string().trim().max(500).default(''),
});
